/**
 * Pure helpers for "are we inside the Discord Activity iframe" and the `/.proxy` network
 * prefix. Dependency-free so drivers, audio and asset loaders can import them without the
 * Discord SDK. Builds on `net/endpoint.ts` (hostname rule) and adds the `frame_id` launch
 * parameter Discord injects into the Activity URL.
 */
import { isEmbedded as embeddedHost } from '../net/endpoint';

export function isEmbedded(loc: { hostname: string; search: string } | undefined = globalThis.location): boolean {
  if (!loc) return false;
  if (embeddedHost(loc.hostname)) return true;
  return new URLSearchParams(loc.search).has('frame_id');
}

/**
 * `?proxy=1` forces the `/.proxy` prefix outside Discord so the URL-mapping path can be exercised
 * against a plain server (`e2e/proxy.spec.ts`). It is deliberately NOT a way to fake being
 * embedded: `isEmbedded()` is unchanged, so the bridge choice and the Discord SDK are unaffected.
 *
 * Honored only in a non-production build, or alongside `?e2e=1` — which already exposes
 * `window.__spades`, a far bigger lever, so this adds no new surface to a real deployment.
 */
export function proxyForced(loc: { hostname: string; search: string } | undefined = globalThis.location): boolean {
  if (!loc) return false;
  const params = new URLSearchParams(loc.search);
  if (params.get('proxy') !== '1') return false;
  return params.get('e2e') === '1' || !isProductionBuild();
}

function isProductionBuild(): boolean {
  // `import.meta.env` is Vite's, and this file is also compiled by plain tsc for the tools build,
  // so it is read defensively rather than through a `vite/client` type reference.
  const meta = import.meta as ImportMeta & { env?: { PROD?: boolean } };
  return meta.env?.PROD === true;
}

/** `'/.proxy'` inside Discord (or under `?proxy=1`), `''` elsewhere. Prefix for every request. */
export function netBase(loc: { hostname: string; search: string } | undefined = globalThis.location): string {
  return isEmbedded(loc) || proxyForced(loc) ? '/.proxy' : '';
}

/** Absolute base for the Colyseus SDK and `fetch` (`${origin}${netBase()}`). */
export function netOrigin(loc: Location | undefined = globalThis.location): string {
  if (!loc) return '';
  return `${loc.origin}${netBase(loc)}`;
}

export function apiPath(path: string, loc: Location | undefined = globalThis.location): string {
  return `${netBase(loc)}${path}`;
}
